import React, {useMemo, useState} from 'react';
import {List} from 'immutable';

import {
  LineData,
  OneRepChartPoint,
  OneRepChartPoints,
} from 'components/dashboard/manager/nine-box/one-rep-chart/OneRepData.utils';
import {DashedLine} from './DashedLine';
import {AvatarTooltipWrapper} from 'components/dashboard/manager/nine-box/avatar/AvatarTooltipWrapper';
import {skillColorNineBox} from 'utils/skill-color-by-idx';
import {KpiGoalPoint} from 'components/dashboard/manager/nine-box/one-rep-chart/KpiGoalPoint';
import {RepChartPoint} from '@store/@types/metric';
import {Moment} from 'moment';
import {PlanId} from 'records/CoachingPlan';
import {GraphGoal} from 'components/dashboard/manager/nine-box/NineBox.utils';
import {Crosshairs} from 'components/dashboard/manager/nine-box/avatar/Crosshairs';

interface OneRepChartProps {
  lineData: List<LineData>;
  data: RepChartPoint;

  chartData?: OneRepChartPoints;
  xGoal?: GraphGoal;
  yGoal?: GraphGoal;

  onCreatePlan: () => void;
  startDate: Moment;
  endDate: Moment;
  setPlanHighlight: (id?: PlanId) => void;
}

const NO_SKILL_COLOR = '#C4C4C4' as const;

interface ScaledLineData {
  color: string;
  from: [number, number];
  to: [number, number];
  planId?: PlanId;
}

/**
 * Shows the rep's earliest and latest data points
 * then plots a line between them split among their coaching plans
 */
export const OneRepChart: React.FC<OneRepChartProps> = ({
  chartData,
  lineData,
  data,
  xGoal,
  yGoal,
  onCreatePlan,
  startDate,
  endDate,
  setPlanHighlight,
}) => {
  const [focusedData, setFocusedData] = useState<OneRepChartPoint>();

  // Parse the fractional size using our line which we derive from the chartData
  const parsedLines: List<ScaledLineData> = useMemo(() => {
    if (!chartData) return List();

    const xLen = chartData.latest.x - chartData.earliest.x;
    const yLen = chartData.latest.y - chartData.earliest.y;

    // Start building our line at the earliest point
    let start: {x: number; y: number} = chartData.earliest;
    let index = 0;
    let lines = List<ScaledLineData>();
    for (const {size, plan} of lineData) {
      const x = start.x + size * xLen;
      const y = start.y + size * yLen;
      lines = lines.push({
        color: plan ? skillColorNineBox(index++) : NO_SKILL_COLOR,
        from: [start.x, start.y], // Start the line at the end of the previous one
        to: [x, y], // End at our calculated value
        planId: plan?.id,
      });
      start = {x, y};
    }
    return lines;
  }, [lineData, chartData]);

  if (!chartData) return null;

  return (
    <>
      <KpiGoalPoint xGoal={xGoal} yGoal={yGoal} latestPoint={chartData.latest} />
      {parsedLines.map(({from, to, color, planId}, i) => (
        <DashedLine
          onMouseEnter={() => setPlanHighlight(planId)}
          onMouseLeave={() => setPlanHighlight()}
          key={`plan-line-${i}`}
          at={[from, to]}
          color={color}
        />
      ))}
      <AvatarTooltipWrapper
        multiRepData={data}
        oneRepData={chartData.earliest}
        onCreatePlan={onCreatePlan}
        startDate={startDate}
        endDate={endDate}
        setCrosshair={show => setFocusedData(show ? chartData?.earliest : undefined)}
      />
      <AvatarTooltipWrapper
        multiRepData={data}
        oneRepData={chartData.latest}
        onCreatePlan={onCreatePlan}
        startDate={startDate}
        endDate={endDate}
        setCrosshair={show => setFocusedData(show ? chartData?.latest : undefined)}
      />
      {focusedData && <Crosshairs data={focusedData} />}
    </>
  );
};
